import type { RenderedView } from '../router.js';
import { navigate } from '../router.js';
import { repos } from '../store.js';
import { esc, onClick, qs, qsa, toast } from '../dom.js';
import { icon } from '../icons.js';
import { brandHero } from '../brand.js';
import { PRESETS } from '../../db/schema.js';

const fmtDate = (ts: number) => new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export async function quizzesView(): Promise<RenderedView> {
  const quizzes = await repos.listQuizzes();
  const [assignments, results, examList] = await Promise.all([
    Promise.all(quizzes.map((q) => repos.assignmentsForQuiz(q.id))),
    Promise.all(quizzes.map((q) => repos.resultsForQuiz(q.id))),
    repos.listTestifyExams(),
  ]);
  const exams = new Map(examList.map((e) => [e.id, e]));

  const body = quizzes.length
    ? `<div class="searchbar">${icon.search()}<input class="input" id="q" placeholder="Search quizzes by title or subject" autocomplete="off"></div>
       <ul class="list" id="qlist">${quizzes
        .map((q, i) => {
          const asg = assignments[i].length;
          const scanned = results[i].length;
          // An imported quiz is one subject of a unified TESTIFY exam — name that exam.
          const exam = q.testifyExamId ? exams.get(q.testifyExamId) : undefined;
          const examName = exam ? exam.examTitle || exam.examCode : q.origin === 'testify' ? 'TESTIFY exam' : '';
          const search = [q.title, q.subject || '', examName].join(' ').toLowerCase();
          return `<button class="row" data-id="${esc(q.id)}" data-search="${esc(search)}"><span class="grow"><span class="ttl">${esc(q.title)}</span>
          <span class="meta">${esc(q.subject || '—')} · ${q.items} items · ${asg} class${asg === 1 ? '' : 'es'}</span>
          ${q.origin === 'testify' ? `<span class="meta"><span class="badge badge-muted">TESTIFY</span> Unified exam · ${esc(examName)}</span>` : ''}
          <span class="meta"><span class="mono">${scanned}</span> scanned · ${esc(fmtDate(q.createdAt))}</span></span><span class="chev">${icon.chevron()}</span></button>`;
        })
        .join('')}</ul>
       <p class="empty" id="nomatch" hidden>No quizzes match.</p>`
    : `<div class="empty">${icon.doc()}<p>No quizzes yet.</p></div>`;

  return {
    title: 'Quizzes',
    tab: 'quiz',
    action: { label: 'About', icon: icon.info(), href: '/about' },
    html: `<div class="view">${brandHero()}
      <div class="row2"><button class="btn btn-primary" data-new>${icon.plus()} New quiz</button>
        <button class="btn btn-ghost" data-testify>${icon.download()} Import TESTIFY</button></div>
      <div style="height:12px"></div>${body}</div>`,
    mount(root) {
      onClick(root, '[data-new]', () => navigate('#/quizzes/new'));
      onClick(root, '[data-testify]', () => navigate('#/testify/import'));
      onClick(root, '.row[data-id]', (el) => navigate(`#/quiz/${el.dataset.id}`));
      qs<HTMLInputElement>(root, '#q')?.addEventListener('input', (e) => {
        const term = (e.target as HTMLInputElement).value.trim().toLowerCase();
        let shown = 0;
        qsa<HTMLElement>(root, '#qlist .row').forEach((el) => {
          const show = !term || (el.dataset.search ?? '').includes(term);
          el.style.display = show ? '' : 'none';
          if (show) shown++;
        });
        const none = qs<HTMLElement>(root, '#nomatch');
        if (none) none.hidden = shown !== 0;
      });
    },
  };
}

export async function createQuizView(): Promise<RenderedView> {
  const classes = await repos.listClasses();
  const today = new Date().toISOString().slice(0, 10);
  const GRADES = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

  return {
    title: 'New quiz',
    back: '/',
    html: `<div class="view view-flush">
      <div class="field"><label for="title">Title</label><input class="input" id="title" maxlength="80" placeholder="e.g. Unit Test 1"></div>
      <div class="row2">
        <div class="field"><label for="subject">Subject</label><input class="input" id="subject" maxlength="60" placeholder="e.g. Math"></div>
        <div class="field"><label for="grade">Grade level</label><select class="input" id="grade"><option value="" selected>Select…</option>${GRADES.map((g) => `<option value="${g}">Grade ${g}</option>`).join('')}</select></div>
      </div>
      <div class="row2">
        <div class="field"><label for="items">Items (sheet preset)</label><select class="input" id="items">${PRESETS.map((p) => `<option value="${p}">${p} items</option>`).join('')}</select></div>
        <div class="field"><label for="date">Date administered</label><input class="input" id="date" type="date" value="${today}"></div>
      </div>
      <p class="section-label">Assign to class(es)</p>
      ${classes.length
        ? `<ul class="list">${classes
            .map(
              (c) => `<li class="row" style="cursor:default"><label style="display:flex;align-items:center;gap:10px;flex:1;min-width:0;cursor:pointer"><input type="checkbox" class="cls" value="${esc(c.id)}" style="width:22px;height:22px;flex:none"><span class="grow"><span class="ttl">${esc(c.name)}</span><span class="meta">Grade ${esc(c.gradeLevel)} · ${esc(c.schoolYear)}</span></span></label></li>`,
            )
            .join('')}</ul>
           <p class="hint">Each sheet's bubbled LRN is read and matched to this class's roster automatically.</p>`
        : `<p class="hint danger-text">No classes yet — create a class first.</p>`}
    </div>`,
    bottom: `<button class="btn btn-primary btn-block" data-create>${icon.check()} Create quiz</button>`,
    mount(root) {
      onClick(root, '[data-create]', async () => {
        const title = qs<HTMLInputElement>(root, '#title')!.value.trim();
        if (!title) return toast('Title is required', 'err');
        const items = Number(qs<HTMLSelectElement>(root, '#items')!.value) as any;
        const date = qs<HTMLInputElement>(root, '#date')!.value || today;
        const classIds = qsa<HTMLInputElement>(root, '.cls:checked').map((c) => c.value);
        if (!classIds.length) return toast('Assign to at least one class', 'err');
        const { quiz } = await repos.createQuiz(
          { title, subject: qs<HTMLInputElement>(root, '#subject')!.value.trim(), gradeLevel: qs<HTMLSelectElement>(root, '#grade')!.value, items },
          // Identity is always the bubbled LRN, auto-matched to the roster ('roster' mode).
          classIds.map((classId) => ({ classId, dateAdministered: date })),
        );
        toast('Quiz created');
        navigate(`#/quiz/${quiz.id}`);
      });
    },
  };
}
